import {
  SizeColumnsToContentStrategy,
  SizeColumnsToFitGridStrategy,
  SizeColumnsToFitProvidedWidthStrategy,
} from "ag-grid-community";

import { Book } from "@bitwarden/web-vault/app/models/data/blobby/book.data";
import { Category } from "@bitwarden/web-vault/app/models/data/blobby/category.data";
import { Classification } from "@bitwarden/web-vault/app/models/data/blobby/classification.data";
import { GlossDateOld } from "@bitwarden/web-vault/app/models/data/shared/gloss-date-old";
import { ConfirmationEnum } from "@bitwarden/web-vault/app/models/enum/confirmation.enum";
import { InputTypes } from "@bitwarden/web-vault/app/models/enum/inputTypes.enum";
import { SyncIcon } from "@bitwarden/web-vault/app/models/enum/sync.enum";
import { TransactionStatusEnum } from "@bitwarden/web-vault/app/models/enum/transactionType";
import { ThirdPartyTransaction } from "@bitwarden/web-vault/app/models/types/transaction.types";
import { AccountView } from "@bitwarden/web-vault/app/models/view/account/account.view";

export type DateString = string;
export type DateFormatIndex = {
  year: number;
  month: number;
  day: number;
  separator: string;
  format: string;
};
export type TimeFormatIndex = {
  hours: number;
  minutes: number;
  seconds?: number;
  is12HourFormat: boolean;
  amPm?: string;
  format: string;
};
export type TimezoneFormatIndex = {
  timezoneName: string;
  timezoneOffset: string; // Store the timezone offset in the format "-05:00"
  timezoneOffsetMinutes?: number; // Store the timezone offset (e.g., -300 for UTC-05:00)
};
export type PossibleDateIndices = {
  forYear: number[];
  forMonth: number[];
  forDay: number[];
};

export type ImportSteps = "upload" | "arrange" | "import" | "institution";
export type MoneySeparatorType = MoneySeparatorValues.decimal | MoneySeparatorValues.thousands;
export enum MoneySeparatorValues {
  decimal = "decimal",
  thousands = "thousands",
}
export type TransactionStatusType = TransactionStatusEnum;
export type Country = { name: string; code: string };

export enum Origin {
  manual = "manual",
  basiq = "basiq",
  plaid = "plaid",
  fiskil = "fiskil",
  plugin = "plugin",
  system = "system",
}

type GlossInputType = InputTypes;

export interface GlossInputOptions {
  value?: string | number;
  suffix?: string;
  label?: string;
  hint?: string;
  placeholder?: string;
  type?: GlossInputType;
  isRequired?: boolean;
  isDisabled?: boolean;
  inputBlurred?: (value: string) => void;
  inputCleared?: () => void;
  inputError?: (message: string) => void;
  onInput: (event: Event) => void;
  hideClearButton?: boolean;
  testId?: string;
  customisedInputOption?: boolean;
}

export type SyncStatusAction = "retry" | "consent" | "connect" | "merge";

export type StatusPointResult = {
  type: "success" | "failure";
  message: string;
  messageI18nKey: string;
  actions: SyncStatusAction[];
};
export type StatusPoint = {
  key: "started-syncing" | "syncing" | "ready" | "synced" | "failed" | "working-on-sync";
  icon: SyncIcon.xCircle | SyncIcon.syncAll | SyncIcon.checkCircle | SyncIcon.alertTriangle;
  data: StatusPointResult;
  lastUpdated?: string;
};

export type SyncStatusKey =
  | SyncStatusEnum.failed
  | SyncStatusEnum.synced
  | SyncStatusEnum.syncing
  | SyncStatusEnum.noBasiqUser
  | SyncStatusEnum.noBasiqConsent
  | SyncStatusEnum.noConnection
  | SyncStatusEnum.noConnectionRefresh
  | SyncStatusEnum.basiqJobFetchFail
  | SyncStatusEnum.dataFetchFail
  | SyncStatusEnum.cantPullTransactions
  | SyncStatusEnum.cantMapTransactions
  | SyncStatusEnum.noTransactionsToProcess
  | SyncStatusEnum.cantSaveCategories
  | SyncStatusEnum.cantValidateTransactions
  | SyncStatusEnum.cantSaveSourceTransactions
  | SyncStatusEnum.cantSaveTransactions
  | SyncStatusEnum.noTransactionsToValidate
  | SyncStatusEnum.cantSaveCBAT
  | SyncStatusEnum.unknownError
  | SyncStatusEnum.noConnector;

export type SyncStatusType = {
  key: SyncStatusKey;
  icon: SyncIcon.xCircle | SyncIcon.syncAll | SyncIcon.checkCircle | SyncIcon.alertTriangle;
  messageKey: string;
  actions: SyncStatusAction[];
  error?: Error;
};

export enum SyncStatusEnum {
  syncing = "syncing",
  synced = "synced",
  failed = "failed",
  noConnector = "noConnector",
  noBasiqUser = "noBasiqUser",
  noBasiqConsent = "noBasiqConsent",
  noConnection = "noConnection",
  noConnectionRefresh = "noConnectionRefresh",
  basiqJobFetchFail = "basiqJobFetchFail",
  dataFetchFail = "dataFetchFail",
  cantPullTransactions = "cantPullTransactions",
  cantMapTransactions = "cantMapTransactions",
  noTransactionsToProcess = "noTransactionsToProcess",
  cantValidateTransactions = "cantValidateTransactions",
  cantSaveCategories = "cantSaveCategories",
  cantSaveSourceTransactions = "cantSaveSourceTransactions",
  cantSaveTransactions = "cantSaveTransactions",
  noTransactionsToValidate = "noTransactionsToValidate",
  unknownError = "unknownError",
  cantSaveCBAT = "cantSaveCBAT",
}

export type AccountState = {
  accountId: string;
  accountView: AccountView;
  rawTransactions: ThirdPartyTransaction[];
  isStarted: boolean;
  isCompleted: boolean;
  isMerged: boolean;
  point: StatusPoint;
  lastSyncedAt: GlossDateOld;
};

export type ScenarioMessageActionType = {
  pre: string;
  text: string;
  post: string;
};

export type ScenarioMessageType = {
  info: string;
  action: ScenarioMessageActionType;
};

export type ScenarioMessagesType = {
  noPositiveBalanceAccounts: ScenarioMessageType;
  noInterestRate: ScenarioMessageType;
  singleAndPositive: ScenarioMessageType;
  singleAndNonPositive: ScenarioMessageType;
  multipleWithOnePositive: ScenarioMessageType;
  notGettingInterest: ScenarioMessageType;
  congratulations: ScenarioMessageType;
};

export type PathMessagesType = {
  [key: number]: ScenarioMessagesType;
};

export type AgGridSizeStrategy =
  | SizeColumnsToFitGridStrategy
  | SizeColumnsToFitProvidedWidthStrategy
  | SizeColumnsToContentStrategy;

export type ConfirmationType = {
  title: string;
  message: string;
  type: "info" | "warning" | "error";
  /** When true, show only one button (e.g. OK) to close the dialog. */
  singleButton?: boolean;
  /**
   * When true, confirm keeps the dialog open, shows loading, then invokes
   * {@link ConfirmationAttachment.stayOpenAfterConfirmAction} after the next paint (if provided).
   */
  stayOpenForPostConfirmAction?: boolean;
  /**
   * When true, the dialog shows a close (X) button and can be dismissed with Esc
   * or a backdrop click. Use with {@link ConfirmationDialogService.choiceFor} to
   * tell a dismissal (null) apart from the "no" button (false).
   */
  dismissible?: boolean;
  /** Custom button labels for yes (confirm) and no (close). */
  customizedButton?: {
    yes: { text: string; class: string };
    no: { text: string; class: string };
  };
  /** When true, the confirm button renders in the destructive (red) style instead of the default accent. */
  destructive?: boolean;
};

// Generate type from enum
export type ConfirmationData = Record<ConfirmationEnum, ConfirmationType>;

export type ConfirmationAttachment = {
  messageReplacement?: string;
  /** Same `$GENERATED$` mechanism as {@link messageReplacement}, but for the dialog's title. */
  titleReplacement?: string;
  templateContent?: any;
  /**
   * Used with {@link ConfirmationType.stayOpenForPostConfirmAction}: called after the loading UI paints.
   * Caller defines behavior (e.g. full page reload).
   */
  stayOpenAfterConfirmAction?: () => void;
};

export type GridStrategy =
  | SizeColumnsToFitGridStrategy
  | SizeColumnsToFitProvidedWidthStrategy
  | SizeColumnsToContentStrategy;

export type MultiSelectableItem = Book | Category | Classification | string;

export function isOfType<T extends string>(value: string, allowedValues: readonly T[]): value is T {
  return allowedValues.includes(value as T);
}
